<script setup lang="ts">
import { ExclamationTriangleIcon } from '@heroicons/vue/24/outline'

import type {
  ManagementDuplicate,
  ManagementDuplicateChoice,
} from '@/types/managementSuggestionTypes'

import { translationFunctionDefault } from '@/utilities/translations'

interface ManagementSuggestionDuplicateChoiceProps {
  choice: ManagementDuplicateChoice
  duplicates: ManagementDuplicate[]
  groupName: string
}

const props = defineProps<ManagementSuggestionDuplicateChoiceProps>()
const emit = defineEmits<{ select: [choice: ManagementDuplicateChoice] }>()

const tfDefault = translationFunctionDefault()

const isLinkChosen = (duplicate: ManagementDuplicate): boolean => {
  return props.choice?.action === 'link' && props.choice.contact === duplicate.name
}

const selectCreate = (): void => {
  emit('select', { action: 'create' })
}

const getOrganizationsText = (duplicate: ManagementDuplicate): string => {
  if (duplicate.organizations.length === 0) {
    return tfDefault('managementSuggestions.duplicateWithoutOrganization')
  }

  return duplicate.organizations.map((organization) => organization.title).join(', ')
}
</script>

<template>
  <div class="flex gap-3 rounded-lg border border-lightning_yellow-400 bg-lightning_yellow-50 p-3">
    <ExclamationTriangleIcon class="h-5 w-5 shrink-0 text-lightning_yellow-500" />
    <fieldset class="flex flex-col gap-2 text-sm text-black-900">
      <legend class="pb-1 font-medium">
        {{ tfDefault('managementSuggestions.duplicateHint') }}
      </legend>
      <label
        v-for="duplicate in duplicates"
        :key="duplicate.name"
        class="flex cursor-pointer items-start gap-2"
      >
        <input
          :checked="isLinkChosen(duplicate)"
          class="mt-0.5 text-lochmara-600 focus:ring-lochmara-600"
          :name="groupName"
          type="radio"
          @change="() => emit('select', { action: 'link', contact: duplicate.name })"
        />
        <span>
          {{
            tfDefault('managementSuggestions.duplicateLink', {
              name: duplicate.full_name ?? duplicate.name,
            })
          }}
          <span class="text-black-500">· {{ getOrganizationsText(duplicate) }}</span>
        </span>
      </label>
      <label class="flex cursor-pointer items-start gap-2">
        <input
          :checked="choice?.action === 'create'"
          class="mt-0.5 text-lochmara-600 focus:ring-lochmara-600"
          :name="groupName"
          type="radio"
          @change="selectCreate"
        />
        <span>{{ tfDefault('managementSuggestions.duplicateCreate') }}</span>
      </label>
    </fieldset>
  </div>
</template>
